import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Detects whether the bound element's content overflows its container along
 * the horizontal axis (i.e., `scrollWidth > clientWidth`).
 *
 * Re-measures via `ResizeObserver` whenever the element resizes. State only
 * updates when the boolean flips, so consumers won't re-render on every tick.
 *
 * Pass `enabled: false` to skip subscribing — useful for callers that gate
 * overflow handling on a prop and don't want to pay the observer cost when
 * disabled.
 *
 * Pass `observedValue` when the measured content can change without the
 * element's box resizing — e.g. a MUI Chip pinned at `max-width`, or a cell in
 * a fixed-width column. `ResizeObserver` stays silent for those, so the state
 * would otherwise go stale against the new content.
 *
 * Companion to {@link useIsClamped}, which detects vertical overflow.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - When false, no observer is created
 *   and `isOverflowing` is always `false`.
 * @param {*} [options.observedValue] - Re-measures whenever this changes, in
 *   addition to on resize. Pass the rendered content (usually the label text).
 * @returns {[boolean, React.MutableRefObject]} Tuple of the current overflow
 *   state and the ref to attach to the measured element.
 */
const useIsOverflowing = ({ enabled = true, observedValue } = {}) => {
  const elementRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = elementRef.current;

    if (!element) return;

    const nextOverflow = element.scrollWidth > element.clientWidth;

    setIsOverflowing((prev) => (prev === nextOverflow ? prev : nextOverflow));
  }, []);

  useLayoutEffect(() => {
    if (!enabled) {
      setIsOverflowing(false);
      return undefined;
    }

    const element = elementRef.current;
    if (!element) return undefined;

    checkOverflow();

    if (typeof window === 'undefined' || !window.ResizeObserver) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [enabled, checkOverflow]);

  // Content can change without the box resizing, which ResizeObserver can't see.
  useLayoutEffect(() => {
    if (enabled) checkOverflow();
  }, [enabled, checkOverflow, observedValue]);

  return [isOverflowing, elementRef];
};

export default useIsOverflowing;
